import { useCallback, useMemo, useState, Dispatch, SetStateAction } from 'react';
import type {
  AttentionItem,
  ReminderItem,
  NoteItem,
  DailyGroup,
  GmailMessage,
  GmailThread,
  WorkContextGraph,
  ResourceRelation,
  ManualWorkHub,
  ResourceId,
  LlmWikiPage,
  CurrentUserIdentity,
} from '../../domain/types';
import { mapGmailToAttentionItem, mapGmailThreadToAttentionItem } from '../../domain/mappers';
import { workspaceClient, gmailClient } from '../../services/clients';
import { DailyService } from '../../services/dailyService';
import { canonicalManualHubResourceId, manualResourceRelation } from '@zunelo/shared';

export interface WorkspaceToolsSliceOptions {
  setItems: Dispatch<SetStateAction<AttentionItem[]>>;
  beginWrite?: () => void;
  retry: (source?: string) => Promise<void>;
  setIsDailyConfirmOpen: (open: boolean) => void;
  currentUser?: CurrentUserIdentity;
  setErrors?: Dispatch<SetStateAction<Record<string, string | null>>>;
}

export function mergeNoteContent(
  note: NoteItem,
  id: string,
  text: string,
  title?: string,
  content?: string,
  due?: string,
  time?: string,
): NoteItem {
  if (note.id !== id) return note;
  return {
    ...note,
    text: text || title?.trim() || note.text,
    title: title?.trim() || note.title,
    content: content?.trim() || note.content,
    due: due !== undefined ? due.trim() || undefined : note.due,
    time: time !== undefined ? time.trim() || undefined : note.time,
  };
}

export function mergeReminderContent(
  reminder: ReminderItem,
  id: string,
  text: string,
  title?: string,
  content?: string,
  due?: string,
  time?: string,
): ReminderItem {
  if (reminder.id !== id) return reminder;
  return {
    ...reminder,
    text: text.trim() || title?.trim() || reminder.text,
    title: title !== undefined ? title.trim() || undefined : reminder.title,
    content: content !== undefined ? content.trim() || undefined : reminder.content,
    due: due !== undefined ? due.trim() || undefined : reminder.due,
    time: time !== undefined ? time.trim() || undefined : reminder.time,
  };
}

export function useWorkspaceToolsSlice({
  setItems,
  beginWrite,
  retry,
  setIsDailyConfirmOpen,
  currentUser,
  setErrors,
}: WorkspaceToolsSliceOptions) {
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [llmWiki, setLlmWiki] = useState<LlmWikiPage[]>([]);
  const [dailyGroups, setDailyGroups] = useState<DailyGroup[]>([]);
  const [dailySent, setDailySent] = useState<boolean>(false);
  const [activeDailyTab, setActiveDailyTab] = useState<'ayer' | 'hoy' | 'bloq'>('hoy');
  const [previewOverride, setPreviewOverride] = useState<string | null>(null);
  const [rawGmailMessages, setRawGmailMessages] = useState<GmailMessage[]>([]);
  const [gmailSummaryOpen, setGmailSummaryOpen] = useState<Record<string, boolean>>({});
  const [gmailSummaryBusy, setGmailSummaryBusy] = useState<Record<string, boolean>>({});
  const [workContextGraph, setWorkContextGraph] = useState<WorkContextGraph>({
    relations: [],
    manualHubs: [],
  });

  // Daily Standup Actions.
  //
  // Every mutator below follows the same shape: compute the next state
  // (and the single changed group to persist) with a pure helper using the
  // CURRENT `dailyGroups` value, call `setDailyGroups(next)` with a plain
  // value (never an updater function), and only THEN persist, outside of
  // any setState callback. React's `<StrictMode>` double-invokes state
  // updater FUNCTIONS in dev; since none of these mutators pass an updater
  // function to `setDailyGroups` anymore, there is no updater for React to
  // double-invoke and `persistDailyGroup` cannot fire twice per
  // interaction. On persistence failure the change is rolled back and
  // surfaced via `retry('workspace')`, which performs a real refetch (see
  // `retrySourceLoad.ts`).
  const persistDailyGroup = useCallback(
    async (previous: DailyGroup[], group: DailyGroup | undefined) => {
      if (!group) return;
      try {
        await workspaceClient.saveDailyGroup(group);
      } catch {
        setDailyGroups(previous);
        await retry('workspace');
      }
    },
    [retry],
  );

  const toggleDailyLine = useCallback(
    (groupId: 'ayer' | 'hoy' | 'bloq', lineId: string) => {
      const previous = dailyGroups;
      const next = DailyService.toggleDailyLine(previous, groupId, lineId);
      setDailyGroups(next);
      void persistDailyGroup(
        previous,
        next.find((g) => g.id === groupId),
      );
    },
    [dailyGroups, persistDailyGroup],
  );

  const addCustomDailyLine = useCallback(
    (groupId: 'ayer' | 'hoy' | 'bloq', text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const previous = dailyGroups;
      const next = DailyService.addDailyCustomLine(previous, groupId, trimmed);
      setDailyGroups(next);
      void persistDailyGroup(
        previous,
        next.find((g) => g.id === groupId),
      );
    },
    [dailyGroups, persistDailyGroup],
  );

  const updateDailyLine = useCallback(
    (groupId: 'ayer' | 'hoy' | 'bloq', lineId: string, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const previous = dailyGroups;
      const next = DailyService.updateDailyLine(previous, groupId, lineId, trimmed);
      setDailyGroups(next);
      void persistDailyGroup(
        previous,
        next.find((g) => g.id === groupId),
      );
    },
    [dailyGroups, persistDailyGroup],
  );

  const linkDailyLine = useCallback(
    (groupId: 'ayer' | 'hoy' | 'bloq', lineId: string, key: string, link: string) => {
      const previous = dailyGroups;
      const next = DailyService.linkDailyLine(previous, groupId, lineId, key, link);
      setDailyGroups(next);
      void persistDailyGroup(
        previous,
        next.find((g) => g.id === groupId),
      );
    },
    [dailyGroups, persistDailyGroup],
  );

  const deleteDailyLine = useCallback(
    (groupId: 'ayer' | 'hoy' | 'bloq', lineId: string) => {
      const previous = dailyGroups;
      const next = DailyService.deleteDailyLine(previous, groupId, lineId);
      setDailyGroups(next);
      void persistDailyGroup(
        previous,
        next.find((g) => g.id === groupId),
      );
    },
    [dailyGroups, persistDailyGroup],
  );

  const setDailyDraft = useCallback((groupId: 'ayer' | 'hoy' | 'bloq', text: string) => {
    setDailyGroups((prev) =>
      prev.map((group) => {
        if (group.id === groupId) {
          return { ...group, draftText: text };
        }
        return group;
      }),
    );
  }, []);

  const sendDaily = useCallback(async () => {
    setDailySent(true);
    setIsDailyConfirmOpen(false);
    try {
      await workspaceClient.sendDaily('#daily-standup');
      setPreviewOverride(null);
    } catch {
      setDailySent(false);
      await retry('workspace');
    }
  }, [setIsDailyConfirmOpen, retry]);

  const saveDailyDraft = useCallback(
    async (markdown: string) => {
      const previous = previewOverride;
      setPreviewOverride(markdown);
      try {
        await workspaceClient.saveDailyDraft(markdown);
      } catch {
        setPreviewOverride(previous);
        await retry('workspace');
      }
    },
    [previewOverride, retry],
  );

  const regenerateDaily = useCallback(async () => {
    setDailySent(false);
    setPreviewOverride(null);
    try {
      await workspaceClient.clearDailySent();
      const res = await workspaceClient.getDailyGroups();
      setDailyGroups(res.data);
    } catch {
      await retry('workspace');
    }
  }, [retry]);

  // Reminders Actions
  const addReminder = useCallback(
    async (
      text: string,
      due?: string,
      time?: string,
      link?: string,
      title?: string,
      content?: string,
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !content?.trim()) return;

      const newReminder: ReminderItem = {
        id: `rem-${Date.now()}`,
        text: trimmed || title?.trim() || 'Recordatorio sin título',
        title: title?.trim() || undefined,
        content: content?.trim() || undefined,
        done: false,
        due: due?.trim() || undefined,
        time: time?.trim() || undefined,
        link,
      };

      setReminders((prev) => [newReminder, ...prev]);
      try {
        await workspaceClient.saveReminder(newReminder);
      } catch {
        // Per-entity rollback: drop only the reminder we optimistically
        // added, never the whole array — concurrent successful edits made
        // by other mutators while this save was pending must survive.
        setReminders((prev) => prev.filter((r) => r.id !== newReminder.id));
        await retry('workspace');
      }
    },
    [retry],
  );

  const toggleReminder = useCallback(
    async (id: string) => {
      let nextDone = true;
      let previousItem: ReminderItem | undefined;
      setReminders((prev) =>
        prev.map((r) => {
          if (r.id === id) {
            previousItem = r;
            nextDone = !r.done;
            return { ...r, done: nextDone };
          }
          return r;
        }),
      );
      try {
        await workspaceClient.toggleReminder(id, nextDone);
      } catch {
        // Per-entity rollback: restore only this reminder's prior snapshot,
        // leaving every other (possibly concurrently-updated) entry intact.
        setReminders((prev) => prev.map((r) => (r.id === id && previousItem ? previousItem : r)));
        await retry('workspace');
      }
    },
    [retry],
  );

  const updateReminder = useCallback(
    async (
      id: string,
      text: string,
      due?: string,
      time?: string,
      title?: string,
      content?: string,
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !content?.trim()) return;

      let updated: ReminderItem | undefined;
      let previousItem: ReminderItem | undefined;
      setReminders((prev) => {
        previousItem = prev.find((reminder) => reminder.id === id);
        const next = prev.map((reminder) =>
          mergeReminderContent(reminder, id, trimmed, title, content, due, time),
        );
        updated = next.find((reminder) => reminder.id === id);
        return next;
      });
      if (updated) {
        try {
          await workspaceClient.saveReminder(updated);
        } catch {
          setReminders((prev) =>
            prev.map((reminder) => (reminder.id === id && previousItem ? previousItem : reminder)),
          );
          await retry('workspace');
        }
      }
    },
    [retry],
  );

  const deleteReminder = useCallback(
    async (id: string) => {
      let previousItem: ReminderItem | undefined;
      let previousIndex = -1;
      setReminders((prev) => {
        previousIndex = prev.findIndex((r) => r.id === id);
        previousItem = prev[previousIndex];
        return prev.filter((r) => r.id !== id);
      });
      try {
        await workspaceClient.deleteReminder(id);
      } catch {
        // Per-entity rollback: re-insert only the deleted reminder at (as
        // close as possible to) its original position. This must not
        // resurrect items deleted by a *different* mutator call that
        // completed while this delete was pending.
        setReminders((prev) => {
          if (!previousItem || prev.some((r) => r.id === id)) return prev;
          const insertAt =
            previousIndex < 0 || previousIndex > prev.length ? prev.length : previousIndex;
          return [...prev.slice(0, insertAt), previousItem, ...prev.slice(insertAt)];
        });
        await retry('workspace');
      }
    },
    [retry],
  );

  // Notes Actions
  const addNote = useCallback(
    async (
      text: string,
      link?: string,
      title?: string,
      content?: string,
      due?: string,
      time?: string,
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !content?.trim()) return;

      const newNote: NoteItem = {
        id: `note-${Date.now()}`,
        text: trimmed || title?.trim() || 'Nota sin título',
        title: title?.trim() || undefined,
        content: content?.trim() || undefined,
        link,
        createdAt: new Date().toISOString(),
        due: due?.trim() || undefined,
        time: time?.trim() || undefined,
      };

      setNotes((prev) => [newNote, ...prev]);
      try {
        await workspaceClient.saveNote(newNote);
      } catch {
        // Per-entity rollback: drop only the note we optimistically added.
        setNotes((prev) => prev.filter((n) => n.id !== newNote.id));
        await retry('workspace');
      }
    },
    [retry],
  );

  const updateNote = useCallback(
    async (
      id: string,
      text: string,
      title?: string,
      content?: string,
      due?: string,
      time?: string,
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !content?.trim()) return;

      let updated: NoteItem | undefined;
      let previousItem: NoteItem | undefined;
      setNotes((prev) => {
        previousItem = prev.find((note) => note.id === id);
        if (previousItem) {
          updated = mergeNoteContent(previousItem, id, trimmed, title, content, due, time);
        }
        return prev.map((note) => mergeNoteContent(note, id, trimmed, title, content, due, time));
      });
      if (updated) {
        try {
          await workspaceClient.saveNote(updated);
        } catch {
          setNotes((prev) =>
            prev.map((note) => (note.id === id && previousItem ? previousItem : note)),
          );
          await retry('workspace');
        }
      }
    },
    [retry],
  );

  const deleteNote = useCallback(
    async (id: string) => {
      let previousItem: NoteItem | undefined;
      let previousIndex = -1;
      setNotes((prev) => {
        previousIndex = prev.findIndex((n) => n.id === id);
        previousItem = prev[previousIndex];
        return prev.filter((n) => n.id !== id);
      });
      try {
        await workspaceClient.deleteNote(id);
      } catch {
        // Per-entity rollback: re-insert only the deleted note; never
        // resurrect items deleted by a different, concurrently-completed call.
        setNotes((prev) => {
          if (!previousItem || prev.some((n) => n.id === id)) return prev;
          const insertAt =
            previousIndex < 0 || previousIndex > prev.length ? prev.length : previousIndex;
          return [...prev.slice(0, insertAt), previousItem, ...prev.slice(insertAt)];
        });
        await retry('workspace');
      }
    },
    [retry],
  );

  const saveLlmWikiPage = useCallback(
    async (page: LlmWikiPage): Promise<LlmWikiPage | null> => {
      try {
        const result = await workspaceClient.saveLlmWikiPage(page);
        setLlmWiki((prev) => [result.data, ...prev.filter((item) => item.id !== result.data.id)]);
        return result.data;
      } catch {
        await retry('workspace');
        return null;
      }
    },
    [retry],
  );

  const deleteLlmWikiPage = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await workspaceClient.deleteLlmWikiPage(id);
        setLlmWiki((prev) => prev.filter((page) => page.id !== id));
        return true;
      } catch {
        await retry('workspace');
        return false;
      }
    },
    [retry],
  );

  const createManualWorkHub = useCallback(
    async (title: string): Promise<ManualWorkHub | null> => {
      const trimmed = title.trim();
      if (!trimmed) return null;
      try {
        const res = await workspaceClient.createManualWorkHub({ title: trimmed });
        setWorkContextGraph((prev) => ({
          relations: prev.relations,
          manualHubs: [...(prev.manualHubs ?? []), res.data],
        }));
        return res.data;
      } catch {
        await retry('workspace');
        return null;
      }
    },
    [retry],
  );

  const attachResourceToHub = useCallback(
    async (memberResourceId: ResourceId, hubId: string): Promise<boolean> => {
      const observedAt = new Date().toISOString();
      const relation = manualResourceRelation(
        memberResourceId,
        canonicalManualHubResourceId(hubId),
        observedAt,
        `manual-attach:${String(memberResourceId)}:${hubId}`,
      );
      try {
        const res = await workspaceClient.upsertWorkContextRelation(relation);
        setWorkContextGraph(res.data);
        return true;
      } catch {
        await retry('workspace');
        return false;
      }
    },
    [retry],
  );

  const detachResourceFromHub = useCallback(
    async (memberResourceId: ResourceId, hubId: string): Promise<boolean> => {
      const observedAt = new Date().toISOString();
      const relation = manualResourceRelation(
        memberResourceId,
        canonicalManualHubResourceId(hubId),
        observedAt,
        `manual-detach:${String(memberResourceId)}:${hubId}`,
        'rejected',
      );
      try {
        const res = await workspaceClient.upsertWorkContextRelation(relation);
        setWorkContextGraph(res.data);
        return true;
      } catch {
        await retry('workspace');
        return false;
      }
    },
    [retry],
  );

  const upsertWorkContextRelation = useCallback(
    async (relation: ResourceRelation): Promise<boolean> => {
      try {
        const res = await workspaceClient.upsertWorkContextRelation(relation);
        setWorkContextGraph(res.data);
        return true;
      } catch {
        await retry('workspace');
        return false;
      }
    },
    [retry],
  );

  // Gmail Actions
  const markGmailRead = useCallback(
    async (id: string) => {
      beginWrite?.();
      setItems((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            const updatedMessages = item.gmailMessages?.map((m) => ({ ...m, unread: false }));
            return { ...item, unread: false, gmailMessages: updatedMessages };
          }
          if (item.gmailMessages?.some((m) => m.id === id)) {
            const updatedMessages = item.gmailMessages.map((m) =>
              m.id === id ? { ...m, unread: false } : m,
            );
            const threadStillUnread = updatedMessages.some((m) => m.unread);
            return { ...item, unread: threadStillUnread, gmailMessages: updatedMessages };
          }
          return item;
        }),
      );
      try {
        await gmailClient.markRead(id);
      } catch {
        await retry('gmail');
      }
    },
    [beginWrite, setItems, retry],
  );

  const replyToGmail = useCallback(
    async (id: string, text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed) return false;
      beginWrite?.();
      setErrors?.((prev) => ({ ...prev, gmail: null }));

      let targetMessageId = id;
      const userDisplayName = currentUser?.displayName || 'Tú';
      const userEmail = currentUser?.email || 'me@zunelo.app';
      const userInitials = currentUser?.displayName
        ? currentUser.displayName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()
        : 'TU';

      setItems((prev) =>
        prev.map((item) => {
          if (item.id !== id && !item.gmailMessages?.some((m) => m.id === id)) return item;
          const msgs = item.gmailMessages || [];
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg) {
            targetMessageId = lastMsg.id;
          }
          const replyMsg: GmailMessage = {
            id: `ML-temp-reply-${Date.now()}`,
            threadId: item.id,
            from: {
              name: userDisplayName,
              email: userEmail,
              initials: userInitials,
              color: 'var(--z-accent)',
            },
            to: lastMsg?.from.email || item.fromEmail || '',
            subject: item.title.startsWith('Re: ') ? item.title : `Re: ${item.title}`,
            bodyParagraphs: [trimmed],
            kind: 'Interno',
            unread: false,
            blocksWork: false,
            receivedAt: new Date().toISOString(),
          };
          const updatedMsgs = [...msgs, replyMsg];
          const body = [...(item.body || []), `Tú (enviado desde Zunelo): ${trimmed}`];
          return {
            ...item,
            unread: false,
            body,
            gmailMessages: updatedMsgs,
            rawGmailMessage: replyMsg,
          };
        }),
      );

      try {
        const res = await gmailClient.replyToMessage(targetMessageId, trimmed);
        setItems((prev) =>
          prev.map((item) => {
            if (item.id !== id && !item.gmailMessages?.some((m) => m.id === id)) return item;
            // If the item had a thread, preserve or replace the temporary reply with real server data
            if (item.rawGmailThread) {
              const msgs = item.gmailMessages?.map((m) =>
                m.id.startsWith('ML-temp-reply-') ? res.data : m,
              ) || [res.data];
              const updatedThread: GmailThread = {
                ...item.rawGmailThread,
                messages: msgs,
                receivedAt: res.data.receivedAt,
                unread: msgs.some((m) => m.unread),
              };
              return mapGmailThreadToAttentionItem(updatedThread, item.st, item.ai, item.next);
            }
            return mapGmailToAttentionItem(res.data, item.st, item.ai, item.next);
          }),
        );
        return true;
      } catch (err) {
        // Failure preserves the composer's draft text (the caller keeps it
        // intact) and drops the optimistic message so the thread reflects
        // what actually happened server-side.
        setItems((prev) =>
          prev.map((item) => {
            if (!item.gmailMessages?.some((m) => m.id.startsWith('ML-temp-reply-'))) return item;
            return {
              ...item,
              gmailMessages: item.gmailMessages.filter((m) => !m.id.startsWith('ML-temp-reply-')),
            };
          }),
        );
        setErrors?.((prev) => ({
          ...prev,
          gmail: err instanceof Error ? err.message : 'No se pudo enviar la respuesta.',
        }));
        return false;
      }
    },
    [beginWrite, setItems, currentUser, setErrors],
  );

  const generateGmailReplyDraft = useCallback(
    async (id: string, hint?: string): Promise<string | null> => {
      setErrors?.((prev) => ({ ...prev, gmail: null }));
      try {
        const res = await gmailClient.generateReplyDraft(id, hint);
        return res.data.draft?.trim() || null;
      } catch (err) {
        setErrors?.((prev) => ({
          ...prev,
          gmail: err instanceof Error ? err.message : 'No se pudo generar el borrador con IA.',
        }));
        return null;
      }
    },
    [setErrors],
  );

  const toggleGmailSummary = useCallback((itemId: string) => {
    setGmailSummaryOpen((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  }, []);

  const isGmailSummaryOpen = useCallback(
    (itemId: string) => !!gmailSummaryOpen[itemId],
    [gmailSummaryOpen],
  );

  /**
   * Short AI-style thread summary, mirroring Slack's `generateThreadSummary`.
   * Preserves the existing summary (`item.ai`) on failure — a failed retry
   * falls back to `retry('gmail')`, which reloads the last-known good value
   * instead of clearing it.
   */
  const generateGmailThreadSummary = useCallback(
    async (itemId: string) => {
      setGmailSummaryOpen((prev) => ({ ...prev, [itemId]: true }));
      setGmailSummaryBusy((prev) => ({ ...prev, [itemId]: true }));
      try {
        const res = await gmailClient.generateThreadSummary(itemId, { force: true });
        setItems((prev) =>
          prev.map((entry) =>
            entry.id === itemId
              ? { ...entry, ai: res.data.summary, next: res.data.actionItem ?? entry.next }
              : entry,
          ),
        );
      } catch {
        await retry('gmail');
      } finally {
        setGmailSummaryBusy((prev) => ({ ...prev, [itemId]: false }));
      }
    },
    [setItems, retry],
  );

  return useMemo(
    () => ({
      reminders,
      setReminders,
      addReminder,
      toggleReminder,
      updateReminder,
      deleteReminder,
      notes,
      setNotes,
      addNote,
      updateNote,
      deleteNote,
      llmWiki,
      setLlmWiki,
      saveLlmWikiPage,
      deleteLlmWikiPage,
      dailyGroups,
      setDailyGroups,
      dailySent,
      setDailySent,
      activeDailyTab,
      setActiveDailyTab,
      toggleDailyLine,
      addCustomDailyLine,
      updateDailyLine,
      linkDailyLine,
      deleteDailyLine,
      setDailyDraft,
      sendDaily,
      saveDailyDraft,
      regenerateDaily,
      previewOverride,
      setPreviewOverride,
      rawGmailMessages,
      setRawGmailMessages,
      markGmailRead,
      replyToGmail,
      toggleGmailSummary,
      isGmailSummaryOpen,
      generateGmailThreadSummary,
      gmailSummaryBusy,
      generateGmailReplyDraft,
      workContextGraph,
      setWorkContextGraph,
      createManualWorkHub,
      attachResourceToHub,
      detachResourceFromHub,
      upsertWorkContextRelation,
    }),
    [
      reminders,
      addReminder,
      toggleReminder,
      updateReminder,
      deleteReminder,
      notes,
      addNote,
      updateNote,
      deleteNote,
      llmWiki,
      saveLlmWikiPage,
      deleteLlmWikiPage,
      dailyGroups,
      dailySent,
      activeDailyTab,
      toggleDailyLine,
      addCustomDailyLine,
      updateDailyLine,
      linkDailyLine,
      deleteDailyLine,
      setDailyDraft,
      sendDaily,
      saveDailyDraft,
      regenerateDaily,
      previewOverride,
      rawGmailMessages,
      markGmailRead,
      replyToGmail,
      toggleGmailSummary,
      isGmailSummaryOpen,
      generateGmailThreadSummary,
      gmailSummaryBusy,
      generateGmailReplyDraft,
      workContextGraph,
      createManualWorkHub,
      attachResourceToHub,
      detachResourceFromHub,
      upsertWorkContextRelation,
    ],
  );
}
